/**
 * Transform a list of API TripLeg objects to a list of simplified app TripLeg objects
 *
 * @param legs The list of API TripLeg objects
 * @returns The list of simplified app TripLeg objects
 */
export function transformTripLegs(legs: ApiSchema['TripLeg'][], coveredLegIds: string[]): TripLeg[] {
    const visibleLegAttributes = ['2', 'BE', 'FL', 'NF', 'R', 'RE', 'RR', 'SV', 'VN', 'X'];
    const disruptionCodePrefix = 'SITUATION';

    return legs
        .filter(leg => leg.timedLeg || leg.transferLeg)
        .map((leg) => {
            if (leg.timedLeg) {
                return {
                    id: leg.id,
                    type: 'timedLeg',
                    origin: {
                        ref: leg.timedLeg.start.stopPlaceRef.stopPlaceRef ?? '',
                        name: leg.timedLeg.start.stopPlaceRef.stopPlaceName ?? '',
                    },
                    destination: {
                        ref: leg.timedLeg.end.stopPlaceRef.stopPlaceRef ?? '',
                        name: leg.timedLeg.end.stopPlaceRef.stopPlaceName ?? '',
                    },
                    departureTime: zurichDate(leg.timedLeg.start.serviceDeparture.timetabledTime),
                    arrivalTime: zurichDate(leg.timedLeg.end.serviceArrival.timetabledTime),
                    originPlatform: leg.timedLeg.start.plannedStopPointName ?? undefined,
                    destinationPlatform: leg.timedLeg.end.plannedStopPointName ?? undefined,
                    direction: leg.timedLeg.service.directions?.[0] ?? undefined,
                    hasEPR: coveredLegIds.includes(leg.id),
                    serviceName: `${leg.timedLeg.service.mode?.shortName ?? ''}${leg.timedLeg.service.lineNumbers?.[0] ?? ''}`,
                    serviceType: leg.timedLeg.service.mode?.ptMode,
                    attributes: leg.timedLeg.attributes
                        ?.filter(a => !!a.code && visibleLegAttributes.includes(a.code))
                        ?.map(a => ({
                            code: a.code ?? '',
                            text: a.text ?? '',
                        }))
                        ?.sort((a, b) => a.code > b.code ? 1 : -1)
                        ?? [],
                    disruption: leg.timedLeg.attributes
                        ?.filter(l => !!l.code && l.code.startsWith(disruptionCodePrefix))
                        ?.map(l => ({
                            title: l.text ?? '',
                            body: l.details ?? '',
                        })) ?? [],
                } satisfies TripLegTimed;
            }
            else if (leg.transferLeg) {
                return {
                    id: leg.id,
                    type: 'transferLeg',
                    origin: {
                        ref: (leg.transferLeg.start as ApiSchema['StopPlaceRef']).stopPlaceRef ?? '',
                        name: (leg.transferLeg.start as ApiSchema['StopPlaceRef']).stopPlaceName ?? '',
                    },
                    destination: {
                        ref: (leg.transferLeg.end as ApiSchema['StopPlaceRef']).stopPlaceRef ?? '',
                        name: (leg.transferLeg.end as ApiSchema['StopPlaceRef']).stopPlaceName ?? '',
                    },
                    departureTime: zurichDate(leg.transferLeg.timeWindowStart),
                    arrivalTime: zurichDate(leg.transferLeg.timeWindowEnd),
                    duration: leg.transferLeg.duration ?? '',
                    mode: leg.transferLeg.transferMode ?? '',
                } satisfies TripLegTransfer;
            }
            else {
                return undefined;
            }
        })
        .filter(Boolean) as TripLeg[]; // Remove empty/undefined objects
}
